import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { adminAPI } from '../../../utils/api';

const Podcasts = () => {
    const { showSuccess, showError } = useNotification();
    const [podcasts, setPodcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPodcast, setEditingPodcast] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        video_url: '',
        thumbnail: '',
        category: 'General',
        is_active: true
    });

    useEffect(() => {
        fetchPodcasts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchPodcasts = async () => {
        try {
            const response = await adminAPI.getAllPodcasts();
            if (response.data.success) {
                setPodcasts(response.data.data || []);
            }
        } catch (error) {
            showError('Failed to load podcasts');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPodcast) {
                await adminAPI.updatePodcast(editingPodcast._id, formData);
                showSuccess('Podcast updated successfully');
            } else {
                await adminAPI.createPodcast(formData);
                showSuccess('Podcast created successfully');
            }
            setShowForm(false);
            setEditingPodcast(null);
            setFormData({ title: '', description: '', video_url: '', thumbnail: '', category: 'General', is_active: true });
            fetchPodcasts();
        } catch (error) {
            showError('Failed to save podcast');
        }
    };

    const handleEdit = (podcast) => {
        setEditingPodcast(podcast);
        setFormData({
            title: podcast.title,
            description: podcast.description || '',
            video_url: podcast.video_url,
            thumbnail: podcast.thumbnail || '',
            category: podcast.category || 'General',
            is_active: podcast.is_active
        });
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this podcast?')) return;
        try {
            await adminAPI.deletePodcast(id);
            showSuccess('Podcast deleted successfully');
            fetchPodcasts();
        } catch (error) {
            showError('Failed to delete podcast');
        }
    };

    if (loading) return <div className="text-center py-8">Loading podcasts...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gold">Manage Podcasts</h1>
                <button
                    onClick={() => {
                        setShowForm(!showForm);
                        setEditingPodcast(null);
                        setFormData({ title: '', description: '', video_url: '', thumbnail: '', category: 'General', is_active: true });
                    }}
                    className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
                >
                    {showForm ? 'Cancel' : 'Add New Podcast'}
                </button>
            </div>

            {showForm && (
                <div className="bg-gray-800 rounded-xl p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gold">{editingPodcast ? 'Edit Podcast' : 'Create New Podcast'}</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <input name="title" value={formData.title} onChange={handleInputChange} placeholder="Title" required className="w-full bg-gray-700 p-2 rounded border border-gray-600" />
                        <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Description" rows={3} className="w-full bg-gray-700 p-2 rounded border border-gray-600" />
                        <input name="video_url" value={formData.video_url} onChange={handleInputChange} placeholder="Video URL (YouTube/Vimeo)" required className="w-full bg-gray-700 p-2 rounded border border-gray-600" />
                        <input name="thumbnail" value={formData.thumbnail} onChange={handleInputChange} placeholder="Thumbnail URL" className="w-full bg-gray-700 p-2 rounded border border-gray-600" />
                        <input name="category" value={formData.category} onChange={handleInputChange} placeholder="Category" className="w-full bg-gray-700 p-2 rounded border border-gray-600" />
                        <div className="flex items-center">
                            <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="mr-2" />
                            <label>Active</label>
                        </div>
                        <button type="submit" className="bg-gold text-dark px-4 py-2 rounded font-bold w-full">{editingPodcast ? 'Update' : 'Create'}</button>
                    </form>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {podcasts.map(podcast => (
                    <div key={podcast._id} className="bg-gray-800 p-4 rounded-xl">
                        <h3 className="text-lg font-bold text-gold">{podcast.title}</h3>
                        <p className="text-sm text-gray-400 line-clamp-2">{podcast.description}</p>
                        <div className="mt-4 flex gap-2">
                            <button onClick={() => handleEdit(podcast)} className="bg-blue-600 px-3 py-1 rounded text-sm">Edit</button>
                            <button onClick={() => handleDelete(podcast._id)} className="bg-red-600 px-3 py-1 rounded text-sm">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Podcasts;
